/**
 * Small, dependency-free environment probes for the PWA push-notification branch.
 * All methods accept optional overrides so unit tests can exercise them without
 * touching the real navigator/window globals.
 */
export class PlatformHelper {
  /**
   * Checks whether the current device is an iPhone / iPad / iPod
   * (including iPadOS, which reports itself as a Mac with touch support)
   * @param userAgent - The user agent string to inspect
   * @param platform - The navigator platform string
   * @param maxTouchPoints - The number of supported touch points
   * @returns True when running on an iOS device
   */
  static isIos(
    userAgent: string = navigator.userAgent,
    platform: string = navigator.platform,
    maxTouchPoints: number = navigator.maxTouchPoints ?? 0
  ): boolean {
    const isIosDevice = /iPad|iPhone|iPod/.test(userAgent);
    const isIpadOs = platform === 'MacIntel' && maxTouchPoints > 1;
    return isIosDevice || isIpadOs;
  }

  /**
   * Checks whether the app is running as an installed PWA (launched from the Home Screen)
   * @returns True when running standalone
   */
  static isStandalone(): boolean {
    const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
    // iOS Safari exposes the legacy navigator.standalone flag.
    const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
    return displayModeStandalone || iosStandalone;
  }

  /**
   * Parses the iOS version from the user agent string
   * @param userAgent - The user agent string to inspect
   * @returns The major/minor iOS version, or null when it cannot be parsed
   */
  static iosVersion(userAgent: string = navigator.userAgent): { major: number; minor: number } | null {
    const match = userAgent.match(/OS (\d+)_(\d+)/);
    if (!match) {
      return null;
    }
    return { major: Number(match[1]), minor: Number(match[2]) };
  }

  /**
   * Checks whether the iOS version supports web push (16.4 is the minimum
   * for Home-Screen web apps)
   * @param userAgent - The user agent string to inspect
   * @returns True when web push is supported (or the version is unknown —
   * the actual subscription API call remains the real gate)
   */
  static supportsIosWebPush(userAgent: string = navigator.userAgent): boolean {
    const version = this.iosVersion(userAgent);
    if (!version) {
      return true;
    }
    return version.major > 16 || (version.major === 16 && version.minor >= 4);
  }

  /**
   * Checks whether the user still needs to install the app to the Home Screen
   * before push can be requested (iOS in a normal Safari tab)
   * @returns True when an iOS device is running in a browser tab
   */
  static needsHomeScreenInstall(): boolean {
    return this.isIos() && !this.isStandalone();
  }
}
